import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import BackArrow from '../assets/images/mobile-back-arrow.svg'
import SortIcon from '../assets/images/mobile-sort.svg'
import FilterIcon from '../assets/images/mobile-filter.svg'

const SORT_OPTIONS = [
  { label: 'Best Seller', value: 'bestseller' },
  { label: 'Newest', value: 'newest-first' },
  { label: 'Low to High', value: 'low-to-high' },
  { label: 'High to Low', value: 'high-to-low' },
]

const FILTER_KEYS = ['categories', 'medicineForms', 'ageGroups', 'brands', 'dietaryNeeds', 'healthInterests']

function MobileProductHeader({ title }) {
  const navigate = useNavigate()
  const location = useLocation()
  const [sortOpen, setSortOpen] = useState(false)
  const [selectedSort, setSelectedSort] = useState(null)
  const [filterOpen, setFilterOpen] = useState(false)
  const [selectedFilters, setSelectedFilters] = useState({
    categories: [],
    medicineForms: [],
    ageGroups: [],
    brands: [],
    dietaryNeeds: [],
    healthInterests: [],
  })
  const { categories, medicineForms, ageGroups, brands, dietaryNeeds, healthInterests } = useSelector((state) => state.product)

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    setSelectedSort(params.get('sortBy') || null)
    setSelectedFilters({
      categories: params.get('categories') ? params.get('categories').split(',') : [],
      medicineForms: params.get('medicineForms') ? params.get('medicineForms').split(',') : [],
      ageGroups: params.get('ageGroups') ? params.get('ageGroups').split(',') : [],
      brands: params.get('brands') ? params.get('brands').split(',') : [],
      dietaryNeeds: params.get('dietaryNeeds') ? params.get('dietaryNeeds').split(',') : [],
      healthInterests: params.get('healthInterests') ? params.get('healthInterests').split(',') : [],
    })
  }, [location.search])

  const buildSearchFromState = (filters, sortBy) => {
    const params = new URLSearchParams()

    FILTER_KEYS.forEach((key) => {
      if (filters[key]?.length > 0) {
        params.set(key, filters[key].join(','))
      }
    })

    if (sortBy) {
      params.set('sortBy', sortBy)
    }

    const query = params.toString()
    return query ? `?${query}` : ''
  }

  const toggleFilter = (groupKey, option) => {
    setSelectedFilters((prevFilters) => {
      const currentValues = prevFilters[groupKey] || []
      const updatedValues = currentValues.includes(option)
        ? currentValues.filter((item) => item !== option)
        : [...currentValues, option]

      return { ...prevFilters, [groupKey]: updatedValues }
    })
  }

  const clearFilters = () => {
    const resetFilters = {
      categories: [],
      medicineForms: [],
      ageGroups: [],
      brands: [],
      dietaryNeeds: [],
      healthInterests: [],
    }

    setSelectedFilters(resetFilters)
    navigate({ pathname: location.pathname, search: buildSearchFromState(resetFilters, selectedSort) })
    setFilterOpen(false)
  }

  const handleApplyFilters = () => {
    navigate({ pathname: location.pathname, search: buildSearchFromState(selectedFilters, selectedSort) })
    setFilterOpen(false)
  }

  const handleSortSelection = (sortValue) => {
    setSelectedSort(sortValue)
    navigate({ pathname: location.pathname, search: buildSearchFromState(selectedFilters, sortValue) })
    setSortOpen(false)
  }

  const filterGroups = [
    {
      title: 'Health Interest',
      items: healthInterests?.data || [],
      key: 'healthInterests',
    },
    {
      title: 'Product Categories',
      items: categories?.data || [],
      key: 'categories',
    },
    {
      title: 'Medicine Form',
      items: medicineForms?.data || [],
      key: 'medicineForms',
    },
    {
      title: 'Age',
      items: ageGroups?.data || [],
      key: 'ageGroups',
    },
    {
      title: 'Brands',
      items: brands?.data || [],
      key: 'brands',
    },
    {
      title: 'Dietary Needs',
      items: dietaryNeeds?.data || [],
      key: 'dietaryNeeds',
    },
  ]

  return (
    <>
      <div className="mobile-product-header">
        <button type="button" className="mph-back" onClick={() => navigate('/')}>
          <img src={BackArrow} alt="Back" />
        </button>
        <h1 className="mph-title">{title}</h1>
        <div className="mph-actions">
          <button type="button" className="mph-action mobile-sorting" onClick={() => setSortOpen(true)}>
            <img src={SortIcon} alt="Sort" />
          </button>
          <button type="button" className="mph-action mobile-filter" onClick={() => setFilterOpen(true)}>
            <img src={FilterIcon} alt="Filter" />
          </button>
        </div>
      </div>

      {sortOpen && (
        <div className="sort-sheet-backdrop" onClick={() => setSortOpen(false)}>
          <div className="sort-sheet" onClick={e => e.stopPropagation()}>
            <div className="sort-sheet-handle" />
            <h3 className="sort-sheet-title">Sort By</h3>
            <ul className="sort-sheet-list">
              {SORT_OPTIONS.map(option => (
                <li key={option.value}>
                  <button
                    type="button"
                    className={`sort-sheet-item${selectedSort === option.value ? ' active' : ''}`}
                    onClick={() => handleSortSelection(option.value)}
                  >
                    {option.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {filterOpen && (
        <div className="sort-sheet-backdrop" onClick={() => setFilterOpen(false)}>
          <div className="filter-sheet" onClick={e => e.stopPropagation()}>
            <div className="sort-sheet-handle" />
            <h3 className="filter-sheet-title">Filters</h3>
            <div className="filter-sheet-body">
              <div className="filter-box">
                <div className="filter-box-body">
                  {filterGroups.map((group) => (
                    <div className="filter-box-group" key={group.title}>
                      <h3>{group.title}</h3>
                      <div className="checkbox-group">
                        {group.items.map((item) => (
                          <div className="form-check custom-checkbox" key={item.slug}>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              value={item.slug}
                              id={item.slug}
                              checked={Boolean(selectedFilters[group.key]?.includes(item.slug))}
                              onChange={() => toggleFilter(group.key, item.slug)}
                            />
                            <label className="form-check-label" htmlFor={item.slug}>
                              {item.title}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="filter-sheet-footer">
              <button type="button" className="filter-sheet-clear" onClick={clearFilters}>Clear All</button>
              <button type="button" className="filter-sheet-apply" onClick={handleApplyFilters}>Apply</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default MobileProductHeader
